import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getErrorMessage } from '../../../packages/api-client';
import {
    addComment, deleteComment, getComments,
    type TaskComment,
} from '../../../packages/api-client/pm-api';
import { getUsers, type User } from '../../../packages/api-client/pm-api';
import { MentionTextarea, renderWithMentions } from '../../../components/MentionTextarea';
import { Compass, Brain, MessageSquare, Bot, Send } from 'lucide-react';
import { attributionLabel, displayName } from './AgentHandoff';

// ─── Comment Thread ───────────────────────────────────────────────────────────

function kindBadge(kind?: string): { icon: React.ReactNode; label: string; color: string } | null {
    if (kind === 'llm_summary') return { icon: <Compass size={14} />, label: 'AI summary', color: 'var(--accent)' };
    if (kind === 'llm_reasoning') return { icon: <Brain size={14} />, label: 'AI reasoning', color: 'var(--accent)' };
    return null;
}

/** The task's comments, shared by the discussion and the agent handoff. */
export function useComments(taskId: string) {
    const [comments, setComments] = useState<TaskComment[]>([]);
    const [error, setError] = useState(false);
    const load = useCallback(() => {
        if (!taskId) return;
        setError(false);
        getComments(taskId).then(setComments).catch(() => setError(true));
    }, [taskId]);
    useEffect(load, [load]);
    return { comments, setComments, error, reload: load };
}

/** canPost gates the composer (tasks:create); canDelete the × per comment (tasks:delete). */
export function CommentThread({ taskId, comments, setComments, error, reload, canPost = true, canDelete = true }: ReturnType<typeof useComments> & { taskId: string; canPost?: boolean; canDelete?: boolean }) {
    const [body, setBody] = useState('');
    const [posting, setPosting] = useState(false);
    const [users, setMentionUsers] = useState<User[]>([]);
    const [showReasoning, setShowReasoning] = useState(false);

    useEffect(() => {
        if (canPost) getUsers(200).then(setMentionUsers).catch(() => {});
    }, [taskId, canPost]);

    async function handlePost() {
        if (!body.trim()) return;
        setPosting(true);
        try {
            const c = await addComment(taskId, body);
            setComments(prev => [...prev, c]);
            setBody('');
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setPosting(false);
        }
    }

    function initials(c: TaskComment) {
        const first = c.author_first_name?.[0] ?? '';
        const last = c.author_last_name?.[0] ?? '';
        const emailInitial = c.author_email?.[0] ?? '?';
        return (first + last || emailInitial).toUpperCase();
    }

    const reasoningCount = comments.filter(c => c.kind === 'llm_reasoning').length;
    const visible = comments.filter(c => c.kind !== 'llm_reasoning' || showReasoning);

    return (
        <div>
            <div style={{ fontWeight: 600, marginBottom: 'var(--space-3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}><MessageSquare size={16} aria-hidden /> Comments ({comments.length})</span>
                {reasoningCount > 0 && (
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setShowReasoning(v => !v)}
                        style={{ fontSize: 'var(--font-size-xs)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}
                    >
                        {showReasoning ? 'Hide' : 'Show'} <Brain size={13} aria-hidden /> reasoning ({reasoningCount})
                    </button>
                )}
            </div>
            {error && (
                <p role="alert" className="task-state-error">
                    Comments couldn't be loaded. <button type="button" className="btn btn-ghost btn-sm" onClick={reload}>Retry</button>
                </p>
            )}
            <div className="comment-thread">
                {visible.map(c => {
                    const badge = kindBadge(c.kind);
                    const isReasoning = c.kind === 'llm_reasoning';
                    const isAI = c.kind === 'llm_reasoning' || c.kind === 'llm_summary';
                    return (
                        <div
                            key={c.id}
                            className="comment-item"
                            style={isReasoning ? { opacity: 0.75, borderLeft: `3px solid ${badge?.color || 'transparent'}`, paddingLeft: 'var(--space-3)' } : undefined}
                        >
                            <div className="comment-avatar" style={isAI ? { background: badge?.color, color: 'var(--on-accent)' } : undefined}>
                                {isAI ? (badge?.icon ?? <Bot size={16} />) : initials(c)}
                            </div>
                            <div className="comment-content">
                                <span className="comment-author">
                                    {isAI ? badge?.label : displayName({ first_name: c.author_first_name, last_name: c.author_last_name, email: c.author_email })}
                                </span>
                                {c.author_agent && <span className="task-chip">{`${c.author_agent} · self-reported`}</span>}
                                {(isAI || (c.auth_channel && c.auth_channel !== 'human') || c.auth_impersonated_by) && (
                                    <span className="comment-time">
                                        {attributionLabel(c.auth_channel, displayName({ first_name: c.author_first_name, last_name: c.author_last_name, email: c.author_email }), c.auth_impersonated_by)}
                                    </span>
                                )}
                                <span className="comment-time">{new Date(c.created_at).toLocaleDateString()}</span>
                                <div className="comment-body" style={isReasoning ? { fontStyle: 'italic', fontSize: 'var(--font-size-sm)' } : undefined}>
                                    {renderWithMentions(c.body)}
                                </div>
                            </div>
                            {canDelete && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)', alignSelf: 'flex-start' }}
                                aria-label="Delete comment"
                                onClick={async () => {
                                    try { await deleteComment(taskId, c.id); setComments(prev => prev.filter(x => x.id !== c.id)); }
                                    catch (err) { toast.error(getErrorMessage(err)); }
                                }}>
                                ×
                            </button>}
                        </div>
                    );
                })}
            </div>
            {canPost && <div className="comment-input-row">
                <MentionTextarea
                    value={body}
                    onChange={setBody}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
                    users={users}
                    placeholder="Write a comment… (@ to mention)"
                />
                <button className="btn btn-primary btn-sm" onClick={handlePost} disabled={posting || !body.trim()}>
                    {posting ? '…' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><Send size={14} /> Send</span>}
                </button>
            </div>}
        </div>
    );
}
